/* eslint-disable no-bitwise */
import { useDispatch, useSelector } from 'react-redux';
import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import { EMPTY_OBJECT } from '../../constants';
import UI from '../../constants/ui';
import getPluralize from '../../helpers/plural';
import Content from '../../components/content';
import { ASSESSMENT_START_ID } from '../../constants/assessmentState';
import StartAssessmentDialog from './dialogs/StartAssessmentDialog';
import { getSkill } from '../../formatter/commonBootstrap';
import preAssessmentCheckHelper from './preAssessmentCheck.helper';
import ErrorSnackBar from '../../components/snackBar/ErrorSnackBar';
import URL from '../../constants/urls';
import { getLoginDetailFromSession } from '../../helpers/sessionDetails';
import LOGIN_TYPE from '../../constants/loginType';
import ASSESSMENT_TYPE, {
  getBinaryAssessmentQType,
} from '../../constants/assessment.constants';
import TECH_ID_START_OF_CUSTOM_ASSESSMENT, {
  TECH_ID_START_OF_AI_JOB_INTERVIEWS,
} from '../createAssessment/Assessment.constant';
import { setshouldShowAlert } from '../../../redux/userSlice';
import AlertDialog from './dialogs/AlertDialog';
import userDevice from './dialogs/Dialog.helper';

function AssessmentDetailsDialog(props) {
  const {
    assessmentDetails,
    showAsCard,
    shouldShowButton,
    shouldShowExtraItems,
    setIsAssessmentModalOpen,
    isSimilarAssessmentScreen,
  } = props;

  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resolvedNames, setResolvedNames] = useState([]);
  const [selectedStartTestTechId, setSelectedStartTestTechId] = useState();
  const [selectedStartTestLanguage, setSelectedStartTestLanguage] = useState();
  const params = new URLSearchParams(window.location.search);
  const entityId = params.get('entityId');
  const loginDetails = getLoginDetailFromSession();
  const [opensnackbar, setSnackbarOpen] = useState();
  const shouldShowAlert = useSelector((state) => state.user.shouldShowAlert);
  const dispatch = useDispatch();

  const handleOkInAlertDialog = () => {
    dispatch(setshouldShowAlert(false));
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen({ setopen: false });
  };

  const [preAssessmentCheckResponse, setPreAssessmentCheckResponse] =
    useState(EMPTY_OBJECT);

  useEffect(() => {
    const filterNames = assessmentDetails?.technologies?.map((eachItem) =>
      getSkill(eachItem)
    );
    if (filterNames) {
      Promise.all(filterNames).then((names) => {
        const joinedNames = names.join(', ');
        setResolvedNames([joinedNames]);
      });
    }
  }, [assessmentDetails]);

  const startTest = (techId) => {
    let url = `${URL.GUIDELINES}?techId=${techId}`;
    if (selectedStartTestLanguage && entityId) {
      url += `&entityId=${entityId}&language=${selectedStartTestLanguage}`;
    } else if (entityId) {
      url += `&entityId=${entityId}`;
    } else if (selectedStartTestLanguage) {
      url += `&language=${selectedStartTestLanguage}`;
    }
    window.open(url, URL.GUIDELINES, 'width=auto,height=auto');

    setIsAssessmentModalOpen(false);
  };

  const preAssessmentCheck = async (techId, language) => {
    setSelectedStartTestTechId(techId);
    setSelectedStartTestLanguage(language);
    setIsLoading(true);
    try {
      const response = await preAssessmentCheckHelper(techId, setSnackbarOpen);
      if (response) {
        setIsStartDialogOpen(true);
        setPreAssessmentCheckResponse(response);
      }
    } catch (error) {
      setSnackbarOpen({
        setopen: true,
        message: error.msg,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartConfirmationClose = () => {
    setIsStartDialogOpen(false);
  };
  const handleStartConfirmation = () => {
    handleStartConfirmationClose();
    startTest(selectedStartTestTechId);
  };

  // const getAssessmentType = (id) => {
  //   const assessment = getAssessmentTypeByID(id);
  //   return assessment;
  // };

  const descriptionText = assessmentDetails?.description
    ? assessmentDetails.description.replace(/(<([^>]+)>)/gi, '')
    : '';
  const loginorLoginCandidate =
    !loginDetails || loginDetails?.role === LOGIN_TYPE.CANDIDATE;

  const binaryOfAssessmentQType = getBinaryAssessmentQType(
    assessmentDetails?.qtype
  );

  return (
    <>
      <div className={!showAsCard && 'mb-3'} key={assessmentDetails?.techId}>
        <div className={`${showAsCard && 'card-border'} px-4 py-3`}>
          <div className="d-flex justify-content-between">
            <div className="fs-4 headline-5-bold color-000000">
              {assessmentDetails?.aiassessment === true &&
              assessmentDetails?.techId >= TECH_ID_START_OF_AI_JOB_INTERVIEWS
                ? UI.AI_INTERVIEW
                : assessmentDetails?.dispName}
            </div>
            {shouldShowExtraItems && (
              <Content condition={assessmentDetails?.resource}>
                <a
                  href={assessmentDetails?.resource}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <AutoStoriesOutlinedIcon />
                </a>
              </Content>
            )}
          </div>
          {assessmentDetails?.description && (
            <Tooltip title={descriptionText}>
              <div className={`body-2 color-5B5B5B my-2 ${'text-truncate'}`}>
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: assessmentDetails?.description,
                  }}
                />
              </div>
            </Tooltip>
          )}
          {resolvedNames.length > 0 &&
            assessmentDetails?.techId > ASSESSMENT_START_ID && (
              <div className=" color-5B5B5B my-2">
                <span className="subtitle-2"> {UI.TECHNOLOGIES}:</span>
                {resolvedNames?.map((eachItem, index) => (
                  <span
                    className="ms-1 subtitle-1-bold color-000000"
                    key={index}
                  >
                    {eachItem}
                  </span>
                ))}
              </div>
            )}
          {assessmentDetails?.aiassessment === true &&
            !isSimilarAssessmentScreen && (
              <div className="subtitle-1 color-5B5B5B my-2">
                <span className="subtitle-1-bold color-000000">
                  {assessmentDetails?.dispName}
                </span>
              </div>
            )}

          {(assessmentDetails?.quesnos && shouldShowExtraItems) ||
          (assessmentDetails?.quesnos && !loginDetails) ? (
            <div className="subtitle-1 my-2 ">
              {binaryOfAssessmentQType.mcq && (
                <div className="d-flex align-items-center my-1">
                  <span className="subtitle-2 color-5B5B5B">
                    {UI.MCQ_QUESTIONS}
                  </span>
                  <div>
                    <span>:&nbsp;</span>
                    <span className="subtitle-1-bold">
                      {assessmentDetails?.quesnos[0]}
                    </span>
                  </div>
                </div>
              )}
              {binaryOfAssessmentQType.coding && (
                <div className="d-flex align-items-center my-1">
                  <span className="subtitle-2 color-5B5B5B">
                    {UI.CODING_QUESTION}
                  </span>
                  <div>
                    <span>:&nbsp;</span>
                    <span className="subtitle-1-bold">
                      {assessmentDetails?.quesnos[1]}
                    </span>
                  </div>
                </div>
              )}
              {binaryOfAssessmentQType.aitext && (
                <div className="d-flex align-items-center my-1">
                  <span className="subtitle-2 color-5B5B5B">
                    {UI.AITEXT_QUESTION}
                  </span>
                  <div>
                    <span>:&nbsp;</span>
                    <span className="subtitle-1-bold">
                      {assessmentDetails?.quesnos[2]}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            ''
          )}

          {assessmentDetails?.aiassessment && (
            <div>
              <span className="subtitle-2 color-5B5B5B"> {UI.QUESTION}:</span>
              <span className="ms-1 subtitle-1-bold color-000000">
                {assessmentDetails.ques}
              </span>
            </div>
          )}

          {shouldShowExtraItems && (
            <div className="my-2">
              <span className="subtitle-2 color-5B5B5B "> {UI.TIME}:</span>

              <span className="ms-1 subtitle-1-bold color-000000">
                {assessmentDetails.time / 60} {UI.MIN}
              </span>
            </div>
          )}

          <div className="subtitle-2 color-5B5B5B">
            <span>{UI.ASSESSMENT_TYPE}:</span>
            <span className="ms-1 subtitle-1-bold color-000000">
              {[
                ((assessmentDetails?.techId >=
                  TECH_ID_START_OF_CUSTOM_ASSESSMENT &&
                  assessmentDetails?.atype ===
                    ASSESSMENT_TYPE.MCQ_ASSESSMENT) ||
                  binaryOfAssessmentQType.mcq) &&
                  UI.ASSESSMENT_TYPE_NAME.MCQ_ASSESSMENT,
                ((assessmentDetails?.techId >=
                  TECH_ID_START_OF_CUSTOM_ASSESSMENT &&
                  assessmentDetails?.atype ===
                    ASSESSMENT_TYPE.CODING_ASSESSMENT) ||
                  binaryOfAssessmentQType.coding) &&
                  UI.ASSESSMENT_TYPE_NAME.CODING_ASSESSMENT,
                assessmentDetails?.techId >=
                  TECH_ID_START_OF_CUSTOM_ASSESSMENT &&
                assessmentDetails?.atype === ASSESSMENT_TYPE.AI_INTERVIEW
                  ? UI.ASSESSMENT_TYPE_NAME.AI_TEXT
                  : (binaryOfAssessmentQType.aitext ||
                      binaryOfAssessmentQType.aivoice ||
                      binaryOfAssessmentQType.aicoding) &&
                    UI.ASSESSMENT_TYPE_NAME.SUBJECTIVE,
              ]
                .filter(Boolean)
                .join(', ')}
            </span>
          </div>

          {loginorLoginCandidate && shouldShowButton && (
            <Button
              className={`${
                loginorLoginCandidate && !showAsCard ? 'w-30' : 'w-100'
              } my-2 button color-1D8FF2`}
              variant={
                loginorLoginCandidate && !showAsCard ? 'contained' : 'outlined'
              }
              onClick={() => {
                if (
                  userDevice() === 'Safari' ||
                  userDevice() === 'iphone' ||
                  userDevice() === 'ipad'
                ) {
                  dispatch(setshouldShowAlert(true));
                } else {
                  preAssessmentCheck(
                    assessmentDetails?.techId,
                    assessmentDetails?.editor
                  );
                }
              }}
              startIcon={
                isLoading && <CircularProgress size="1rem" color="inherit" />
              }
            >
              {!assessmentDetails?.aiassessment
                ? UI.START_TEST
                : UI.START_AI_INTERVIEW_TEST}
            </Button>
          )}
          {shouldShowExtraItems && (
            <div
              className={`${
                showAsCard && 'justify-content-center'
              }  d-flex  caption color-5B5B5B`}
            >
              <span>
                {UI.REQUIRES} {assessmentDetails?.value}{' '}
                {getPluralize(UI.POINT, assessmentDetails?.value)}
              </span>
            </div>
          )}
        </div>
      </div>
      <StartAssessmentDialog
        isDialogOpen={isStartDialogOpen}
        primaryAction={handleStartConfirmation}
        secondaryAction={handleStartConfirmationClose}
        content={preAssessmentCheckResponse.msg}
        primaryLabel={preAssessmentCheckResponse.allowed ? UI.YES : ''}
        secondaryLabel={preAssessmentCheckResponse.allowed ? UI.NO : UI.CLOSE}
      />

      <AlertDialog
        isDialogOpen={shouldShowAlert}
        primaryAction={handleOkInAlertDialog}
      />
      <ErrorSnackBar opensnackbar={opensnackbar} handleClose={handleClose} />
    </>
  );
}

AssessmentDetailsDialog.propTypes = {
  assessmentDetails: PropTypes.object,
  showAsCard: PropTypes.bool,
  shouldShowButton: PropTypes.bool,
  shouldShowExtraItems: PropTypes.bool,
  setIsAssessmentModalOpen: PropTypes.func,
  isSimilarAssessmentScreen: PropTypes.bool,
};

AssessmentDetailsDialog.defaultProps = {
  assessmentDetails: EMPTY_OBJECT,
  showAsCard: true,
  shouldShowButton: true,
  shouldShowExtraItems: true,
  setIsAssessmentModalOpen: noop,
  isSimilarAssessmentScreen: false,
};

export default AssessmentDetailsDialog;
